// Coach-Governed Continuity Loop — carry-forward candidate view model builder.
// Hard boundaries: no scoring, no ranking, no diagnosis, no intent/emotion inference,
// no personality labeling, no automatic memory creation, no persistence changes.
// Do NOT create conclusions about the leader or coach.
// Do NOT include evidenceSources in CarryForwardCandidate.

import type { CarryForwardCandidate } from "@/types/carryForward";
import type { EvidenceFragment, EvidenceFragmentSourceType } from "@/types/evidenceFragment";
import { createEvidenceFragment } from "@/types/evidenceFragment";
import type { LeadershipRecordItem, LeadershipRecordSourceType } from "./buildLeadershipRecordItems";

export const EVALUATION_PROHIBITION = "Do not evaluate this leader's performance or effectiveness";

const DEFAULT_LIMIT = 5;
/**
 * Minimum candidates to return — a product invariant.
 * Fewer than MIN_CANDIDATES produces a poor ritual experience.
 * Note: this overrides an explicit `limit` when items are available.
 */
const MIN_CANDIDATES = 3;

const CONFIDENCE_ORDER: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

/** Map LeadershipRecordSourceType to EvidenceFragmentSourceType */
function mapSourceType(sourceType: LeadershipRecordSourceType): EvidenceFragmentSourceType {
  switch (sourceType) {
    case "bi_observation":
      return "bi_observation";
    case "context_graph_node":
      return "context_graph_node";
    case "leader_memory_event":
      return "leader_memory_event";
    case "practice_summary":
      return "practice_summary";
    case "transcript_evidence":
      return "transcript";
    case "reflection":
      return "reflection";
    case "coach_note":
      return "coach_note";
    case "shared_summary":
      return "practice_summary";
  }
}

function buildEvidenceFragments(item: LeadershipRecordItem): EvidenceFragment[] {
  const sourceType = mapSourceType(item.sourceType);
  // When no display labels are available, fall back to the item's own sourceId so
  // persistence lineage is always preserved (avoids upsertContinuityReview falling
  // back to the "coach_chat" sentinel for transcript/reflection/coach-note moments).
  if (!item.evidenceSources || item.evidenceSources.length === 0) {
    return [createEvidenceFragment(sourceType, item.sourceId, { title: item.title })];
  }
  // evidenceSources holds display labels ("Transcript: Title"), not DB IDs.
  // Use item.sourceId as the canonical identifier; keep the label as displayLabel.
  return item.evidenceSources.map((label) =>
    createEvidenceFragment(sourceType, item.sourceId, { title: item.title, displayLabel: label })
  );
}

function sortByConfidenceAndRecency(items: LeadershipRecordItem[]): LeadershipRecordItem[] {
  return [...items].sort((a, b) => {
    const confA = CONFIDENCE_ORDER[a.confidence ?? "low"] ?? 1;
    const confB = CONFIDENCE_ORDER[b.confidence ?? "low"] ?? 1;
    if (confB !== confA) return confB - confA;
    const timeA = Date.parse(a.occurredAt || a.createdAt || "");
    const timeB = Date.parse(b.occurredAt || b.createdAt || "");
    return timeB - timeA;
  });
}

function buildCandidateFromItem(
  item: LeadershipRecordItem,
  leaderId?: string,
  coachId?: string,
  mode: "leader" | "coach_practice" = "leader",
): CarryForwardCandidate {
  const evidenceFragments = buildEvidenceFragments(item);

  const missingData: string[] = [];

  const uncertainty: string[] = item.uncertainty ? [...item.uncertainty] : [];

  // uncertainty[] carries the raw uncertainty strings for machine/AI use.
  // whatNotToAssume[] carries the same strings re-framed as explicit coach guardrails.
  // Both are populated from the same source until LeadershipRecordItem carries distinct data.
  const whatNotToAssume: string[] = item.uncertainty
    ? item.uncertainty.map((u) => `Do not assume: ${u}`)
    : [];

  const prohibitedClaims: string[] = [EVALUATION_PROHIBITION];

  return {
    id: `carry-forward-${item.id}`,
    leaderId: mode === "coach_practice" ? undefined : leaderId ?? item.leaderId,
    coachId,
    mode,
    title: item.title,
    observedMoment: item.observedFact,
    whySurfaced: item.whySurfaced ?? "Appeared in recent session evidence.",
    evidenceFragments,
    missingData,
    uncertainty,
    whatNotToAssume,
    prohibitedClaims,
    sourceRecordItemIds: [item.id],
    sourceTable: item.carryForwardSourceTable,
    sourceRowId: item.carryForwardSourceRowId,
  };
}

/**
 * Converts existing LeadershipRecordItem[] into
 * CarryForwardCandidate[].
 *
 * Returns 3–5 candidates by default (or fewer if fewer items are available).
 * Prefers items with higher confidence and more recent occurredAt.
 *
 * Hard boundaries: no scoring, no ranking, no diagnosis, no intent/emotion inference,
 * no personality labeling. Do not create conclusions about the leader or coach.
 *
 * @param params.limit - maximum to return; the result will contain at least MIN_CANDIDATES
 *   when items are available, regardless of limit.
 */
export function buildCarryForwardCandidates(params: {
  items: LeadershipRecordItem[];
  leaderId?: string;
  coachId?: string;
  mode?: "leader" | "coach_practice";
  limit?: number;
}): CarryForwardCandidate[] {
  const { items, leaderId, coachId, mode = "leader", limit = DEFAULT_LIMIT } = params;

  if (items.length === 0) return [];

  const effectiveLimit = Math.max(MIN_CANDIDATES, Math.min(limit, items.length));
  const sorted = sortByConfidenceAndRecency(items);
  const selected = sorted.slice(0, effectiveLimit);

  const candidates = selected.map((item) =>
    buildCandidateFromItem(item, leaderId, coachId, mode)
  );

  return candidates;
}
